import { PairName } from '@ui/assets/config/trade-pair-listings';
import { tryReportError } from '@ui/lib/bugsnag';
import { getLimitOrderFacet } from '@ui/services/contract/contract-instances/getLimitOrderFacet';
import { ChainId } from '@ui/types/chain';
import { BareTradePair } from '@ui/types/tradePair';
import {
  Nullable,
  PromiseBatchResult,
  PromiseSuccess,
} from '@ui/types/utility';
import { invariant } from '@ui/utils/invariant';
import { Address, PublicClient } from 'viem';

import { toOrder } from './toOrder';
import { ContractOrder, Order } from './types';

export const GET_LIMIT_ORDERS_BATCH = 'LimitOrderFacet.getLimitOrders.batch';
export const getLimitOrdersBatchQuery = {
  getQueryKey: ({
    chainId,
    pairs,
    user,
    clientUid,
  }: {
    chainId: number;
    user: Address;
    pairs: Record<PairName, BareTradePair> | undefined;
    clientUid: string;
  }) => [
    GET_LIMIT_ORDERS_BATCH,
    chainId,
    Object.values(pairs ?? {})
      .map((p) => p.pairName)
      .join(','),
    user,
    clientUid,
  ],
  getQueryFn:
    ({
      pairs,
      user,
      client,
      chainId,
    }: {
      chainId: ChainId;
      user: Address;
      pairs: Record<PairName, BareTradePair> | undefined;
      client: Nullable<PublicClient>;
    }) =>
    async (): Promise<Order[]> => {
      invariant(client);
      const contract = getLimitOrderFacet({
        publicClient: client,
        chainId,
      });

      const result: PromiseBatchResult<ContractOrder[]> =
        await client.multicall({
          contracts: Object.values(pairs ?? {}).map((p) => ({
            abi: contract.abi,
            address: contract.address,
            functionName: 'getLimitOrders',
            args: [user, p.address],
          })),
        });

      const baseToPair = Object.fromEntries(
        Object.entries(pairs ?? {}).map(([key, value]) => [
          value.address,
          value,
        ]),
      );

      return result
        .filter((p): p is PromiseSuccess<ContractOrder[]> => {
          if (p.status !== 'success') {
            tryReportError(p.error);

            return false;
          }

          return true;
        })
        .flatMap((res) =>
          res.result.map((p) =>
            toOrder(p, chainId, baseToPair[p.pairBase.toLowerCase()]),
          ),
        );
    },
};
